// This file provides a centralized way to access environment variables

interface WindowWithEnv extends Window {
  __ENV?: Record<string, string>
}

// Define the shape of our public environment variables
export interface PublicEnv {
  NEXT_PUBLIC_API_GATEWAY_URL: string
  NEXT_PUBLIC_GRAPHQL_API_URL: string
  NEXT_PUBLIC_KEYCLOAK_REALM: string
  NEXT_PUBLIC_KEYCLOAK_URL: string
  NEXT_PUBLIC_KEYCLOAK_CLIENT_ID: string
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: string
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: string
  NEXT_PUBLIC_SENTRY_DSN: string
  NEXT_PUBLIC_LANDING_VARIANT: string
  NEXT_PUBLIC_BRAND_LOGO: string
  NEXT_PUBLIC_BRAND_NAME: string
  NEXT_PUBLIC_META_TITLE: string
  NEXT_PUBLIC_META_DESCRIPTION: string
  NEXT_PUBLIC_DEFAULT_LANGUAGE: string
  NEXT_PUBLIC_UNLEASH_URL: string
  NEXT_PUBLIC_UNLEASH_CLIENT_KEY: string
  NEXT_PUBLIC_UNLEASH_APP_NAME: string
  NEXT_PUBLIC_UNLEASH_ENVIRONMENT: string
}

// Initialize environment with default values
export const defaultEnv: PublicEnv = {
  NEXT_PUBLIC_API_GATEWAY_URL: 'http://localhost:3000/api',
  NEXT_PUBLIC_GRAPHQL_API_URL: 'http://localhost:3000/api/graphql/patient',
  NEXT_PUBLIC_KEYCLOAK_REALM: 'patients',
  NEXT_PUBLIC_KEYCLOAK_URL: 'http://localhost:8080',
  NEXT_PUBLIC_KEYCLOAK_CLIENT_ID: 'auth',
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: '',
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: '',
  NEXT_PUBLIC_SENTRY_DSN: '',
  NEXT_PUBLIC_LANDING_VARIANT: 'default',
  NEXT_PUBLIC_BRAND_LOGO: '',
  NEXT_PUBLIC_BRAND_NAME: 'Medconnect Platform',
  NEXT_PUBLIC_META_TITLE: 'Medconnect Patient',
  NEXT_PUBLIC_META_DESCRIPTION: "Medconnect Patient's portal, book appointment, access teleconsultation, find your medical records and more.",
  NEXT_PUBLIC_DEFAULT_LANGUAGE: 'fr',
  // Unleash is optional — empty URL/key disables it (all flags default to false)
  NEXT_PUBLIC_UNLEASH_URL: '',
  NEXT_PUBLIC_UNLEASH_CLIENT_KEY: '',
  NEXT_PUBLIC_UNLEASH_APP_NAME: 'medconnect-patient',
  NEXT_PUBLIC_UNLEASH_ENVIRONMENT: 'local',
}

// Function to ensure URL has a protocol
export function ensureProtocol(url: string): string {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  // If it's a relative URL starting with /, prepend the base URL
  if (url.startsWith('/')) {
    return `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'}${url}`
  }
  // Otherwise, assume http://
  return `http://${url}`
}

/**
 * Gets an environment variable, supporting both Vite (VITE_) and Next.js (NEXT_PUBLIC_) prefixes
 * and finally to a provided default value
 */
export function getEnvVar(key: keyof PublicEnv, defaultValue: string = ''): string {
  try {
    // Try window.__ENV first (runtime injection from Docker/env.sh takes priority)
    if (typeof window !== 'undefined') {
      const windowEnv = (window as WindowWithEnv).__ENV
      if (windowEnv && windowEnv[key]) return windowEnv[key]
      const viteKey = key.replace('NEXT_PUBLIC_', 'VITE_')
      if (windowEnv && windowEnv[viteKey]) return windowEnv[viteKey]
    }

    // Fall back to Vite environment variables (build-time)
    const metaEnv = import.meta.env as Record<string, string> | undefined
    if (metaEnv) {
      const viteKey = key.replace('NEXT_PUBLIC_', 'VITE_')
      if (metaEnv[viteKey]) return metaEnv[viteKey]
      if (metaEnv[key]) return metaEnv[key]
    }

    // Fall back to process.env (for tests)
    if (typeof process !== 'undefined' && process.env) {
      if (process.env[key]) return process.env[key] as string
    }
  } catch (err) {
    console.warn(`[getEnvVar] Error while trying to get env var ${key}:`, err)
  }

  return defaultValue || (defaultEnv[key] as string)
}

/**
 * Utility function to safely access environment variables with fallbacks.
 * Accepts any string key (unlike getEnvVar which is typed to PublicEnv keys).
 */
export function getEnv(key: string, fallback: string = ''): string {
  try {
    // Try window.__ENV first (runtime injection from Docker/env.sh takes priority)
    if (typeof window !== 'undefined') {
      const windowEnv = (window as WindowWithEnv).__ENV
      if (windowEnv && typeof windowEnv === 'object') {
        if (windowEnv[key]) return windowEnv[key]
        const viteKey = key.replace('NEXT_PUBLIC_', 'VITE_')
        if (windowEnv[viteKey]) return windowEnv[viteKey]
      }
    }

    // Fall back to Vite environment variables (build-time)
    const metaEnv = import.meta.env as Record<string, string> | undefined
    if (metaEnv) {
      const viteKey = key.replace('NEXT_PUBLIC_', 'VITE_')
      if (metaEnv[viteKey]) return metaEnv[viteKey]
      if (metaEnv[key]) return metaEnv[key]
    }

    // Fall back to process.env (for tests/node environment)
    if (typeof process !== 'undefined' && process.env) {
      if (process.env[key]) return process.env[key] as string
    }
  } catch (err) {
    // Ignore errors accessing env
  }

  return fallback
}

/**
 * Get boolean environment variable with fallback
 */
export function getBooleanEnv(key: string, fallback: boolean = false): boolean {
  const value = getEnv(key)
  if (!value) return fallback

  return value.toLowerCase() === 'true' || value === '1'
}

// Export the environment variables
export const env: PublicEnv = {
  get NEXT_PUBLIC_API_GATEWAY_URL() {
    return ensureProtocol(getEnvVar('NEXT_PUBLIC_API_GATEWAY_URL'))
  },
  get NEXT_PUBLIC_GRAPHQL_API_URL() {
    return ensureProtocol(getEnvVar('NEXT_PUBLIC_GRAPHQL_API_URL'))
  },
  get NEXT_PUBLIC_KEYCLOAK_REALM() {
    return getEnvVar('NEXT_PUBLIC_KEYCLOAK_REALM')
  },
  get NEXT_PUBLIC_KEYCLOAK_URL() {
    return getEnvVar('NEXT_PUBLIC_KEYCLOAK_URL')
  },
  get NEXT_PUBLIC_KEYCLOAK_CLIENT_ID() {
    return getEnvVar('NEXT_PUBLIC_KEYCLOAK_CLIENT_ID')
  },
  get NEXT_PUBLIC_GOOGLE_MAPS_API_KEY() {
    return getEnvVar('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY')
  },
  get NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY() {
    return getEnvVar('NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY')
  },
  get NEXT_PUBLIC_SENTRY_DSN() {
    return getEnvVar('NEXT_PUBLIC_SENTRY_DSN')
  },
  get NEXT_PUBLIC_LANDING_VARIANT() {
    return getEnvVar('NEXT_PUBLIC_LANDING_VARIANT')
  },
  get NEXT_PUBLIC_BRAND_LOGO() {
    return getEnvVar('NEXT_PUBLIC_BRAND_LOGO')
  },
  get NEXT_PUBLIC_BRAND_NAME() {
    return getEnvVar('NEXT_PUBLIC_BRAND_NAME')
  },
  get NEXT_PUBLIC_META_TITLE() {
    return getEnvVar('NEXT_PUBLIC_META_TITLE')
  },
  get NEXT_PUBLIC_META_DESCRIPTION() {
    return getEnvVar('NEXT_PUBLIC_META_DESCRIPTION')
  },
  get NEXT_PUBLIC_DEFAULT_LANGUAGE() {
    return getEnvVar('NEXT_PUBLIC_DEFAULT_LANGUAGE')
  },
  get NEXT_PUBLIC_UNLEASH_URL() {
    return getEnvVar('NEXT_PUBLIC_UNLEASH_URL')
  },
  get NEXT_PUBLIC_UNLEASH_CLIENT_KEY() {
    return getEnvVar('NEXT_PUBLIC_UNLEASH_CLIENT_KEY')
  },
  get NEXT_PUBLIC_UNLEASH_APP_NAME() {
    return getEnvVar('NEXT_PUBLIC_UNLEASH_APP_NAME')
  },
  get NEXT_PUBLIC_UNLEASH_ENVIRONMENT() {
    return getEnvVar('NEXT_PUBLIC_UNLEASH_ENVIRONMENT')
  },
}
